import React, { useEffect, useMemo, useState } from 'react';
import services from '../services';
import SandboxedEmail from './SandboxedEmail';
import BookingReplyDropdown from './BookingReplyDropdown';

/**
 * Shows the SAME booking email thread the Daily Departures screen shows, inside a
 * kanban ticket, split into Supplier / Customer / Employee tabs, plus Reply / Forward
 * using the shared mail reply UI (common/ReplyDropdown via BookingReplyDropdown).
 *
 * Source: bookingConversations (keyed by bookingId). The thread is pre-attached to the task
 * response as `bookingThread` (backend task.service getTaskById) and refetched after a reply
 * via /bookingConversation/byBooking/:bookingId.
 */

const IMAGE_URL = process.env.REACT_APP_IMAGE_URL || '';
const getFileUrl = (file) => `${IMAGE_URL}/${typeof file === 'string' ? file : file?.path || file?.filename || file?.url || ''}`;

// '"Name" <a@b.com>' -> 'a@b.com'
const pickAddress = (str) => {
    if (!str) return '';
    return (String(str).match(/<([^>]+)>/)?.[1] || String(str).replace(/"/g, '')).trim();
};

const fmtDate = (d) => {
    if (!d) return '';
    try {
        return new Date(d).toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true,
        });
    } catch {
        return '';
    }
};

// Split a mail body into the new text (main) and the quoted older thread (rest),
// so the quote can be collapsed behind "..." — same behaviour as the normal ticket
// (MailInbox.processMailBody), which fixes the big blank gap on reply rows.
const splitQuotedBody = (body) => {
    if (!body) return { main: '', rest: '', count: 0 };
    const formatMain = (t) => (!t ? '' : (!/<[a-z][\s\S]*>/i.test(t) ? t.replace(/(\r\n|\r|\n)/g, '<br/>') : t));

    if (/<[a-z][\s\S]*>/i.test(body)) {
        try {
            const doc = new DOMParser().parseFromString(body, 'text/html');
            let quoteNode = doc.body.querySelector('blockquote') || doc.body.querySelector('.gmail_quote, .gmail_attr');
            const attrPattern = /\b(On [\s\S]{0,80}?wrote:|El [\s\S]{0,80}?escribi[oó]:|[\s\S]{0,80}?escreveu:|Von:[\s\S]{0,300}?Betreff:|From:[\s\S]{0,300}?Subject:|De:[\s\S]{0,300}?Objet:)/i;
            const bodyText = doc.body.textContent || '';
            const attrMatch = bodyText.match(attrPattern);
            if (attrMatch) {
                const walker = document.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
                let acc = 0; let node; let attrNode = null;
                while ((node = walker.nextNode())) {
                    const len = node.textContent.length;
                    if (acc + len > attrMatch.index) { attrNode = node; break; }
                    acc += len;
                }
                const attrEl = attrNode?.parentElement;
                if (attrEl && (!quoteNode || (attrEl.compareDocumentPosition(quoteNode) & Node.DOCUMENT_POSITION_FOLLOWING))) {
                    quoteNode = attrEl;
                }
            }
            if (quoteNode) {
                const path = [];
                let n = quoteNode;
                while (n && n.parentNode && n !== doc.body) {
                    path.unshift(Array.from(n.parentNode.childNodes).indexOf(n));
                    n = n.parentNode;
                }
                const bodyClone = doc.body.cloneNode(true);
                let restContainer = bodyClone;
                let mainPart = '';
                for (let i = 0; i < path.length; i++) {
                    const children = Array.from(restContainer.childNodes);
                    const before = children.slice(0, path[i]);
                    const meaningful = before.filter((nd) => {
                        if (nd.nodeType === Node.TEXT_NODE) return true;
                        if (nd.querySelector?.('img')) return true;
                        return (nd.textContent || '').trim().length > 0;
                    });
                    mainPart += meaningful.map((nd) => (nd.outerHTML !== undefined ? nd.outerHTML : nd.textContent)).join('');
                    before.forEach((nd) => restContainer.removeChild(nd));
                    restContainer = restContainer.childNodes[0];
                }
                mainPart = mainPart.trim();
                const restPart = (bodyClone.innerHTML || '').trim();
                const mainVisible = mainPart.replace(/<[^>]*>/g, '').replace(/\s+/g, '').length;
                if (restPart && mainVisible >= 20) {
                    return { main: formatMain(mainPart), rest: formatMain(restPart), count: 1 };
                }
            }
        } catch (e) { /* fall through */ }
    }

    const pattern = /\b(On [\s\S]*?wrote:|El [\s\S]*?escribi[oó]:|[\s\S]*?escreveu:)/i;
    const idx = body.search(pattern);
    if (idx < 0) return { main: formatMain(body), rest: '', count: 0 };
    return { main: formatMain(body.slice(0, idx).trim()), rest: formatMain(body.slice(idx).trim()), count: 1 };
};

const renderAttachments = (files, title) => {
    if (!files) return null;
    let arr = [];
    if (Array.isArray(files)) arr = files;
    else if (typeof files === 'string') arr = [files];
    else if (typeof files === 'object') arr = [files];
    if (!arr.length) return null;
    return (
        <div className="mt-2">
            <div style={{ fontWeight: 600, fontSize: 12, color: '#1f6feb', marginBottom: 4 }}>{title}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {arr.map((file, i) => {
                    const name = typeof file === 'string'
                        ? (file.split('/').pop() || file)
                        : (file?.originalName || file?.name || file?.filename || `File-${i + 1}`);
                    return (
                        <a key={i} href={getFileUrl(file)} target="_blank" rel="noreferrer"
                            style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #e5e7eb', borderRadius: 6, textDecoration: 'none', color: '#334155', background: '#f8fafc' }}>
                            📎 {name}
                        </a>
                    );
                })}
            </div>
        </div>
    );
};

const MessageItem = ({ item, bookingThread, expanded, onToggle, onSent }) => {
    const [replyMode, setReplyMode] = useState(null); // null | 'reply' | 'forward'
    const [quoteOpen, setQuoteOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const rawFrom = item?.data?.email?.from || item?.sentBy?.name || item?.sentBy?.email || 'Unknown';
    const senderName = rawFrom.includes('<') ? rawFrom.split('<')[0].trim() : (rawFrom.split('@')[0] || rawFrom);
    const senderEmail = pickAddress(rawFrom) || rawFrom;
    const rawTo = item?.data?.email?.to || item?.sentTo?.email || 'Unknown';
    const rawHtml = item?.data?.email?.bodyHtml || item?.data?.email?.body || item?.data?.taskMeta?.channelData?.action?.body || '';
    // old replies quoted the sender as an object -> "[object Object] wrote:"; show the email instead
    const html = useMemo(() => (rawHtml || '').replace(/\[object Object\]/g, senderEmail || ''), [rawHtml, senderEmail]);
    const { main, rest, count } = useMemo(() => splitQuotedBody(html), [html]);
    const snippet = html.replace(/<[^>]+>/g, '').substring(0, 100).trim() + (html.length > 100 ? '…' : '');
    const avatar = (senderName?.charAt(0) || 'U').toUpperCase();

    // outlined green pill, matching the normal ticket's Reply / Forward buttons
    const pillBtnStyle = { display: 'inline-flex', alignItems: 'center', gap: 8, border: '1px solid #1a7d3e', color: '#1a7d3e', background: '#fff', borderRadius: 999, padding: '8px 18px', fontSize: 14, fontWeight: 500, cursor: 'pointer' };

    return (
        <div style={{ borderBottom: '1px solid #eef2f7', padding: '12px 14px' }}>
            <div style={{ display: 'flex', cursor: 'pointer' }} onClick={onToggle}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#e2e8f0', color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, marginRight: 12, flexShrink: 0 }}>
                    {avatar}
                </div>
                <div style={{ flexGrow: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ minWidth: 0 }}>
                            <span style={{ fontWeight: 700, color: '#111827' }}>{senderName}</span>{' '}
                            <span style={{ color: '#6b7280', fontSize: 12 }}>&lt;{senderEmail}&gt;</span>
                            {expanded ? (
                                <div style={{ color: '#6b7280', fontSize: 12, marginTop: 2 }}>To <span style={{ color: '#111827' }}>{rawTo}</span></div>
                            ) : (
                                <div style={{ color: '#6b7280', fontSize: 13, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{snippet}</div>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                            <span style={{ color: '#9ca3af', fontSize: 12 }}>{fmtDate(item?.data?.email?.sentAt || item?.createdAt)}</span>
                            <span className="mail-reading-action-btn" title="Reply" onClick={() => { setReplyMode('reply'); if (!expanded) onToggle(); }} style={{ cursor: 'pointer', display: 'inline-flex' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="#444" strokeWidth="1.5" viewBox="0 0 24 24">
                                    <path d="M10 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M3 12h13a6 6 0 0 1 6 6v1" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                            <span className="mail-reading-action-btn relative" title="More options" onClick={() => setMenuOpen((o) => !o)} style={{ cursor: 'pointer', display: 'inline-flex', position: 'relative' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="#444" strokeWidth="1.5" viewBox="0 0 24 24">
                                    <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
                                </svg>
                                {menuOpen && (
                                    <div style={{ position: 'absolute', top: 24, right: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 20, minWidth: 150, padding: 6 }}>
                                        <button onClick={(e) => { e.stopPropagation(); setMenuOpen(false); setReplyMode('reply'); if (!expanded) onToggle(); }} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', border: 'none', background: 'transparent', padding: '8px 10px', fontSize: 14, color: '#334155', cursor: 'pointer' }}>
                                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M10 19l-7-7 7-7M3 12h13a6 6 0 0 1 6 6v1" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                            Reply
                                        </button>
                                        <button onClick={(e) => { e.stopPropagation(); setMenuOpen(false); setReplyMode('forward'); if (!expanded) onToggle(); }} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', border: 'none', background: 'transparent', padding: '8px 10px', fontSize: 14, color: '#334155', cursor: 'pointer' }}>
                                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M14 5l7 7-7 7M21 12H8a6 6 0 0 0-6 6v1" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                            Forward
                                        </button>
                                    </div>
                                )}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {expanded && (
                <div style={{ marginTop: 12, marginLeft: 52 }} onClick={(e) => e.stopPropagation()}>
                    <SandboxedEmail className="mail-reading-body" html={main || html} />
                    {count >= 1 && (
                        <>
                            <button
                                type="button"
                                className="mail-reading-expand-btn"
                                onClick={() => setQuoteOpen((o) => !o)}
                                title={quoteOpen ? 'Hide quoted text' : 'Show quoted text'}
                                style={{ border: '1px solid #e5e7eb', background: '#f8fafc', borderRadius: 6, padding: '0 8px', cursor: 'pointer', color: '#64748b', margin: '6px 0' }}
                            >
                                <span>…</span>
                            </button>
                            {quoteOpen && <SandboxedEmail className="mail-reading-body gmail-thread" html={rest} />}
                        </>
                    )}
                    {item?.notes && (
                        <div style={{ marginTop: 10, padding: 10, background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 6 }}>
                            <div style={{ fontWeight: 700, fontSize: 12, color: '#111827', marginBottom: 4 }}>Internal Note</div>
                            <SandboxedEmail html={item.notes} />
                        </div>
                    )}
                    {renderAttachments(item?.uploadConfirmationProof || item?.data?.uploadConfirmationProof || item?.data?.email?.uploadConfirmationProof, 'Confirmation Proof')}
                    {renderAttachments(item?.uploadInvoceProof || item?.data?.uploadInvoceProof || item?.data?.email?.uploadInvoceProof, 'Invoice')}

                    {!replyMode ? (
                        <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
                            <button onClick={() => setReplyMode('reply')} style={pillBtnStyle}>
                                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><path d="M10 19l-7-7 7-7M3 12h13a6 6 0 0 1 6 6v1" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                Reply
                            </button>
                            <button onClick={() => setReplyMode('forward')} style={pillBtnStyle}>
                                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><path d="M14 5l7 7-7 7M21 12H8a6 6 0 0 0-6 6v1" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                Forward
                            </button>
                        </div>
                    ) : (
                        <BookingReplyDropdown
                            item={item}
                            isForward={replyMode === 'forward'}
                            onCancel={() => setReplyMode(null)}
                            onSent={() => { setReplyMode(null); onSent && onSent(); }}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

const BookingThread = ({ bookingThread }) => {
    const [rows, setRows] = useState(bookingThread?.conversations || []);
    const [expanded, setExpanded] = useState(null);
    const [loading, setLoading] = useState(false);
    // audience tabs, same split as the Daily Departures screen
    const [tab, setTab] = useState('SUPPLIER');

    const allEmailRows = useMemo(
        () => (rows || []).filter((r) => (r?.converSationType || 'email') === 'email'),
        [rows]
    );

    // messageType filter per tab (mirrors backend getByBookingId: SUPPLIER also matches null/empty)
    const matchesTab = (r, t) => {
        const mt = r?.messageType;
        if (t === 'SUPPLIER') return mt === 'SUPPLIER' || mt == null || mt === '';
        return mt === t;
    };

    const emailRows = useMemo(() => allEmailRows.filter((r) => matchesTab(r, tab)), [allEmailRows, tab]);

    const tabCounts = useMemo(() => ({
        SUPPLIER: allEmailRows.filter((r) => matchesTab(r, 'SUPPLIER')).length,
        CUSTOMER: allEmailRows.filter((r) => matchesTab(r, 'CUSTOMER')).length,
        EMPLOYEE: allEmailRows.filter((r) => matchesTab(r, 'EMPLOYEE')).length,
    }), [allEmailRows]);

    useEffect(() => {
        setRows(bookingThread?.conversations || []);
    }, [bookingThread]);

    useEffect(() => {
        setExpanded(emailRows.length ? emailRows.length - 1 : null);
    }, [emailRows.length, tab]);

    const fetchOnce = async () => {
        const res = await services.bookingConversation.GET_BY_BOOKING(bookingThread.bookingId, 'flag=email&falsePagination=true');
        // controller shape: { success, message, data: [...rows], pagination }
        const data = res?.data?.data || res?.data?.rows || [];
        return Array.isArray(data) ? data : [];
    };

    // A reply is sent via Gmail and only lands back in the thread once the PubSub webhook
    // writes it (async), so poll a few times before giving up.
    const refresh = async () => {
        if (!bookingThread?.bookingId) return;
        setLoading(true);
        const startCount = allEmailRows.length;
        try {
            for (const delay of [0, 2000, 4000, 7000]) {
                if (delay) await new Promise((r) => setTimeout(r, delay));
                try {
                    const data = await fetchOnce();
                    if (data.length) setRows(data);
                    const emailCount = data.filter((r) => (r?.converSationType || 'email') === 'email').length;
                    if (emailCount > startCount) break; // new reply showed up
                } catch (e) {
                    // 404 (none yet) or transient — keep polling
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const TABS = [
        { key: 'SUPPLIER', label: 'Supplier' },
        { key: 'CUSTOMER', label: 'Customer' },
        { key: 'EMPLOYEE', label: 'Employee' },
    ];

    const tabBar = (
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e5e7eb', padding: '0 8px', background: '#fff', position: 'sticky', top: 0, zIndex: 1 }}>
            {TABS.map((t) => (
                <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    style={{
                        border: 'none',
                        background: 'transparent',
                        padding: '10px 14px',
                        fontSize: 13,
                        cursor: 'pointer',
                        fontWeight: tab === t.key ? 700 : 500,
                        color: tab === t.key ? '#16a34a' : '#64748b',
                        borderBottom: tab === t.key ? '2px solid #16a34a' : '2px solid transparent',
                    }}
                >
                    {t.label}{tabCounts[t.key] ? ` (${tabCounts[t.key]})` : ''}
                </button>
            ))}
        </div>
    );

    return (
        <div style={{ background: '#fff', borderRadius: 8, maxHeight: 'calc(100vh - 150px)', overflowY: 'auto', paddingBottom: 40 }}>
            {tabBar}
            {emailRows.length ? (
                emailRows.map((item, idx) => (
                    <MessageItem
                        key={item.id || idx}
                        item={item}
                        bookingThread={bookingThread}
                        expanded={expanded === idx}
                        onToggle={() => setExpanded(expanded === idx ? null : idx)}
                        onSent={refresh}
                    />
                ))
            ) : (
                <div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
                    No {tab.toLowerCase()} email yet.{loading && <span> Loading…</span>}
                </div>
            )}
        </div>
    );
};

export default BookingThread;
